import { Moment } from 'moment'
import Modernizr from 'modernizr'

export interface Selected {
  type: 'selected'
  selected: [string]
}

export interface Checked {
  type: 'checked'
  checked: boolean
}

export interface Greater {
  type: 'greater'
  greaterThan: number
}

export interface Between {
  type: 'between'
  min: number
  max: number
}

export interface Not {
  type: 'not'
  not: any
}

type Requirement = Selected | Checked | Greater | Between | Not

export interface FieldDependency {
  field: string,
  requirement: Requirement,
}

export interface FieldDependencies {
  type: 'either' | 'both'
  dependencies: FieldDependency[]
}

export type FieldValue = string | boolean | number | Moment | undefined

export interface FieldSpec {
  field: string,
  required: boolean,
  when: 'always' | FieldDependencies,
}

export interface Field {
  name: string,
  value?: string | boolean,
  store: boolean,
}

export function initFieldRequirements (name: string): FieldSpec {
  return {
    field: name,
    required: false,
    when: 'always',
  }
}

export function initField (name: string): Field {
  return {
    name,
    value: undefined,
    store: false,
  }
}

export function requirementPasses(requirement: Requirement, value: string | boolean): boolean {
  switch (requirement.type) {
    case 'selected': {
      if (typeof value !== 'string') {
        throw new Error(`Requirement is 'selected', but value is not a string`)
      }
      return requirement.selected.includes(value)
    }
    case 'checked': {
      if (typeof value !== 'boolean') {
        throw new Error(`Requirement is 'checked', but value is not a boolean`)
      }
      return requirement.checked === value
    }
    case 'greater': {
      if (typeof value !== 'string') {
        throw new Error(`Requirement is 'greater', but value ${value} is not a string`)
      }
      let parsed = parseFloat(value);
      if (isNaN(parsed)) {
        return false;
      }
      return parsed > requirement.greaterThan
    }
    case 'between': {
      if (typeof value !== 'string') {
        throw new Error(`Requirement is 'between', but value is not a string`)
      }
      let parsed = parseFloat(value);
      if (isNaN(parsed)) {
        return false;
      }
      return parsed >= requirement.min && parsed <= requirement.max
    }
    case 'not': {
      if (typeof requirement.not === 'number' && typeof value === 'string') {
        let parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return false
        }
        return parsed !== requirement.not;
      } else if (typeof value !== typeof requirement.not) {
        return false
      } else {
        return value !== requirement.not
      }
    }
  }
}

export function loadSpecs(config: any): FieldSpec[] {
  function parseFieldDependency(dependency: any): FieldDependency {
    let requirement: Requirement | undefined

    if (typeof dependency.selected === 'string') {
      requirement = {
        type: 'selected',
        selected: [dependency.selected],
      };
    } else if (dependency.selected instanceof Array) {
      requirement = {
        type: 'selected',
        selected: dependency.selected ,
      };
    } else if (dependency.checked !== undefined) {
      requirement = {
        type: 'checked',
        checked: dependency.checked,
      };
    } else if (dependency.greater !== undefined) {
      requirement = {
        type: 'greater',
        greaterThan: dependency.greater,
      };
    } else if (dependency.between !== undefined) {
      requirement = {
        type: 'between',
        min: dependency.between.min,
        max: dependency.between.max,
      };
    } else if (dependency.not !== undefined) {
      requirement = {
        type: 'not',
        not: dependency.not,
      };
    } else if (dependency.selected !== undefined) {
      throw new Error(`Could not parse when.selected field dependency. Value must be string or array, but is ${typeof dependency.selected}`)
    }

    return {
      field: dependency.field,
      requirement,
    } as FieldDependency
  }

  let specs: FieldSpec[] = []

  for (let field in config) {
    let spec = config[field]
    let required: boolean = spec.required
    let when: 'always' | FieldDependencies

    if (spec.when === undefined || spec.when === 'always') {
      when = 'always'
    } else if (spec.when.either) {
      let dependencies = []
      for (let dependency of spec.when.either) {
        dependencies.push(parseFieldDependency(dependency))
      }
      when = {
        type: 'either',
        dependencies,
      }
    } else if (spec.when.both) {
      let dependencies = []
      for (let dependency of spec.when.both) {
        dependencies.push(parseFieldDependency(dependency))
      }
      when = {
        type: 'both',
        dependencies,
      }
    } else {
      when = {
        type: 'either',
        dependencies: [parseFieldDependency(spec.when)],
      }
    }

    let value = ''

    specs.push({ field, required, when })
  }

  return specs
}

export function loadFields(config: any): Field[] {
  let fields: Field[] = []

  for (let name in config) {
    let spec = config[name]
    let value = spec.default
    let store = spec.store

    if (typeof value === 'number') {
      value = `${value}`
    }

    if (Modernizr.localstorage && store) {
      const storedValue = window.localStorage.getItem(name)
      if (storedValue !== null) {
        if (storedValue == 'true') {
          value = true
        } else if (storedValue == 'false') {
          value = false
        } else {
          value = storedValue
        }
      }
    }

    fields.push({ name, value, store })
  }

  return fields
}
